(() => {
  'use strict';

  const ROOT_SELECTOR = '.page-content-back';
  const BACK_LINK_SELECTOR = '[data-cslt-back]';

  /** Same origin as the storefront (ignores malformed referrer URLs). */
  const isSameOrigin = (url) => {
    try {
      return new URL(url, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  };

  /**
   * Whether back should use session history / referrer instead of the link href.
   * Edge cases:
   * - Direct open / new tab with no referrer → false (use href fallback, e.g. /pages/shop).
   * - External referrer (Google, email) with history.length === 1 → false.
   * - Same-origin referrer with history.length === 1 (e.g. opened PDP in new tab from PLP) → true.
   * - Referrer equals current URL (reload) → false.
   * - history.length > 1 → true (normal in-site navigation; prefer history.back()).
   */
  const hasMeaningfulBackHistory = () => {
    if (window.history.length > 1) return true;

    const referrer = document.referrer;
    if (!referrer || !isSameOrigin(referrer)) return false;

    try {
      const from = new URL(referrer);
      const here = new URL(window.location.href);
      return from.pathname !== here.pathname || from.search !== here.search;
    } catch {
      return false;
    }
  };

  const bindBackLinks = (root = document) => {
    root.querySelectorAll(BACK_LINK_SELECTOR).forEach((back) => {
      if (back.dataset.csltBackBound === 'true') return;
      back.dataset.csltBackBound = 'true';

      back.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }
        if (!hasMeaningfulBackHistory()) return;

        event.preventDefault();
        if (window.history.length > 1) {
          window.history.back();
          return;
        }

        const referrer = document.referrer;
        if (referrer && isSameOrigin(referrer)) {
          window.location.assign(referrer);
        }
      });
    });
  };

  const navOffsetPx = () => {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--site-nav-offset')
      .trim();
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : 0;
  };

  const applyFixed = (root) => {
    const top = navOffsetPx() + parseFloat(root.dataset.pcbInnerTop || '0');
    const left = parseFloat(root.dataset.pcbPinLeft || '0');
    root.style.setProperty('--page-content-back-top', `${top}px`);
    root.style.setProperty('--page-content-back-left', `${left}px`);
    root.classList.add('page-content-back--fixed');
  };

  const measureRoot = (root) => {
    root.classList.remove('page-content-back--fixed');
    const rect = root.getBoundingClientRect();
    root.dataset.pcbInnerTop = String(rect.top - navOffsetPx());
    root.dataset.pcbPinLeft = String(rect.left);
    applyFixed(root);
  };

  const syncAll = (remeasure) => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (remeasure || root.dataset.pcbInnerTop == null) {
        measureRoot(root);
      } else {
        applyFixed(root);
      }
    });
  };

  const init = () => {
    bindBackLinks();
    syncAll(true);

    window.addEventListener('resize', () => syncAll(window.scrollY === 0), { passive: true });
    window.addEventListener('site-nav-offset-sync', () => {
      syncAll(window.scrollY === 0);
    });

    const navWrapper = document.querySelector('.nav-wrapper');
    if (navWrapper && typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => syncAll(false)).observe(navWrapper);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => syncAll(window.scrollY === 0)).catch(() => {});
    }
  };

  const runInit = () => requestAnimationFrame(init);

  // Defer scripts run at "interactive", before DOMContentLoaded — do not init on rAF alone.
  if (document.readyState === 'complete') {
    runInit();
  } else {
    document.addEventListener('DOMContentLoaded', runInit);
  }
})();
